/**
 * Help Data — single source of truth for the in-app help drawer.
 * Mirrors the XTS pattern (sections × topics, route-aware defaults)
 * but uses PlayStudio's IconName instead of FontAwesome.
 */

import type { IconName } from "../icons/Icon";

export interface HelpTopic {
  id: string;
  title: string;
  /** Body is an HTML string, rendered via dangerouslySetInnerHTML. */
  body: string;
}

export interface HelpSection {
  id: string;
  title: string;
  icon: IconName;
  topics: HelpTopic[];
}

export const helpSections: HelpSection[] = [
  // ─────────────────────────────────────────────────────────────── WELCOME
  {
    id: "welcome",
    title: "Welcome",
    icon: "sparkle",
    topics: [
      {
        id: "getting-started",
        title: "Getting Started",
        body: `
          <p>Welcome to <strong>PlayStudio</strong> — the execution layer of <strong>Xamun Intelligence (XI)</strong>. XI decides <em>what</em> to do; PlayStudio is where you make it happen.</p>

          <p style="margin-top:14px; font-weight:600; color:var(--accent);">Your first 10 minutes</p>
          <ol style="margin-top:6px; padding-left:18px;">
            <li style="margin-top:6px;"><strong>Open the Dashboard</strong> — see your greeting, the one decision that needs you, your in-flight plays, and replans awaiting sign-off.</li>
            <li style="margin-top:6px;"><strong>Check the Inbox</strong> — initiatives promoted from XI land here. Each one has a projected KPI, target window, signal lineage, and cohort.</li>
            <li style="margin-top:6px;"><strong>Plan an initiative</strong> — click <em>Plan it</em> to start a Discovery interview, or <em>Open in Editor</em> to skip straight to the visual planner with the design fixture pre-loaded. For complex initiatives that need multiple concurrent plays, click <em>Convert to GamePlan</em> instead to group them under one shared context.</li>
            <li style="margin-top:6px;"><strong>Walk the Editor</strong> — Timeline / Graph / Kanban views of the step graph. Inspect any step, bind agents, set decision gates.</li>
            <li style="margin-top:6px;"><strong>Review &amp; launch</strong> — pre-flight checklist (Pattern Library match, stakeholders, KPI, agent coverage, decision gates), then <em>Launch play</em>.</li>
            <li style="margin-top:6px;"><strong>Watch it run</strong> — autonomous steps fire via the dispatcher fabric (custom_webhook / Copilot / cowork_mcp). Human-owned steps surface a Claim button to the right operator.</li>
            <li style="margin-top:6px;"><strong>Sign off when complete</strong> — outcome capture aggregates KPI delta, lessons learned, and pushes to XI's governance bus.</li>
          </ol>

          <div style="background:var(--bg-soft); border:1px solid var(--border); border-radius:8px; padding:12px; margin-top:14px;">
            <p style="color:var(--accent); font-weight:600; margin:0 0 6px;">Three demo workspaces are pre-loaded</p>
            <ul style="font-size:12.5px; margin:0; padding-left:16px;">
              <li><strong>ACME Corp (Demo)</strong> — process domain · cost program archetype</li>
              <li><strong>BrewHaus Coffee Co. (Demo)</strong> — market_and_brand domain · competitive counter</li>
              <li><strong>Clarion CX (Demo)</strong> — market_and_brand domain · retention recovery</li>
            </ul>
            <p style="font-size:12px; color:var(--text-3); margin:6px 0 0;">Each comes with 6 plays in mixed states (running / replanning / paused / draft / completed / abandoned), real step graphs, and bound agents.</p>
          </div>
        `,
      },
      {
        id: "playstudio-vs-xi",
        title: "PlayStudio vs XI",
        body: `
          <p>XI and PlayStudio are siblings. They share an identity, a strategy map, and a roadmap, but they own different jobs.</p>

          <table style="width:100%; border-collapse:collapse; margin-top:12px; font-size:12.5px;">
            <thead>
              <tr style="background:var(--bg-soft);">
                <th style="text-align:left; padding:8px; border:1px solid var(--border);">Xamun Intelligence (XI)</th>
                <th style="text-align:left; padding:8px; border:1px solid var(--border);">PlayStudio</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="padding:8px; border:1px solid var(--border); vertical-align:top;">
                  Decides <strong>what</strong>. Diagnostic, Objectives, Roadmap, Change Management, Decisions, Org, Inbox, Docs, Sources.
                </td>
                <td style="padding:8px; border:1px solid var(--border); vertical-align:top;">
                  Decides <strong>how</strong>. Plays, Steps, Agents, Outcomes, Governance.
                </td>
              </tr>
              <tr>
                <td style="padding:8px; border:1px solid var(--border); vertical-align:top;">
                  Owns the <strong>strategy map</strong>: objectives → KPIs → initiatives.
                </td>
                <td style="padding:8px; border:1px solid var(--border); vertical-align:top;">
                  Owns the <strong>execution graph</strong>: plays → steps → agents → outcomes.
                </td>
              </tr>
              <tr>
                <td style="padding:8px; border:1px solid var(--border); vertical-align:top;">
                  Routes software initiatives to the <strong>Software Factory</strong>.
                </td>
                <td style="padding:8px; border:1px solid var(--border); vertical-align:top;">
                  Receives non-software initiatives (gtm / process / capability / org_design / recruitment / training / vendor / operational).
                </td>
              </tr>
            </tbody>
          </table>

          <p style="margin-top:12px;">The handoff is one-way: XI promotes an initiative → PlayStudio's Inbox receives it as a <code>roadmap_entry</code> → user plans it → outcomes flow back to XI's lineage bus.</p>
        `,
      },
      {
        id: "personas",
        title: "Personas &amp; Roles",
        body: `
          <p>PlayStudio surfaces are persona-aware — the left nav, the actions you see, and the home page all adapt to your role on the active workspace.</p>

          <ul style="margin-top:10px; padding-left:18px;">
            <li style="margin-top:6px;"><strong>CXO / Owner</strong> — sees the full Dashboard, can approve/start/pause/abandon plays, request replans, switch domains.</li>
            <li style="margin-top:6px;"><strong>Operator</strong> — claims human-owned steps, captures decisions, runs queries, drafts comms.</li>
            <li style="margin-top:6px;"><strong>Approver</strong> — reviews steps in <em>awaiting_review</em> state, approves or requests changes.</li>
            <li style="margin-top:6px;"><strong>Reviewer / Staff</strong> — read-only visibility into plays they're a stakeholder on, plus their own personal home page.</li>
          </ul>

          <p style="margin-top:12px;">Your role is set on each workspace membership. A user can be CXO on their own workspace and Operator on a partner's.</p>
        `,
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────── MODULES
  {
    id: "modules",
    title: "Modules",
    icon: "dashboard",
    topics: [
      {
        id: "dashboard-module",
        title: "Dashboard",
        body: `
          <p>The Dashboard is your daily landing page. It answers one question: <em>what needs my attention right now?</em></p>

          <ul style="margin-top:10px; padding-left:18px;">
            <li><strong>Greeting</strong> — time-aware, uses your first name (derived from display name or email local-part).</li>
            <li><strong>The one thing</strong> — the highest-priority lede: count of decisions needing you, replans awaiting sign-off, or "all clear".</li>
            <li><strong>In-flight plays</strong> — every play not in <em>completed</em> / <em>abandoned</em> with state pill, replan count, target window.</li>
            <li><strong>Replans awaiting sign-off</strong> — replan cards with rationale + diff link.</li>
            <li><strong>Today's activity</strong> — live ws fan-out: step events, decisions, agent dispatches.</li>
          </ul>
        `,
      },
      {
        id: "inbox-module",
        title: "Inbox",
        body: `
          <p>The Roadmap Inbox is where initiatives promoted by XI land. Three filter tabs:</p>

          <ul style="margin-top:8px; padding-left:18px;">
            <li><strong>Unplanned</strong> (default) — status = <code>committed</code>. These need a plan.</li>
            <li><strong>Deferred</strong> — status = <code>deferred</code>, with a deferred-until date and reason.</li>
            <li><strong>Planned</strong> — status in (<code>in_execution</code>, <code>completed</code>, <code>abandoned</code>).</li>
          </ul>

          <p style="margin-top:10px;"><strong>Per-entry actions:</strong></p>
          <ul style="padding-left:18px;">
            <li><strong>Plan it</strong> — POSTs to <code>/plan-it</code>. If an archetype matches → drops a draft play and routes you to the play workspace. Otherwise starts a Discovery session and routes to the chat.</li>
            <li><strong>Open in Editor</strong> — skips Discovery and lands you on the planner Editor with the design fixture pre-loaded. Useful for previewing the editor with rich data.</li>
            <li><strong>Defer</strong> — moves the entry to the Deferred tab with a reason + revisit date.</li>
            <li><strong>More context</strong> — sends a request back to XI for additional signal lineage.</li>
            <li><strong>Reassign</strong> — change owner.</li>
            <li><strong>Open in XI ↗</strong> — deep-link to the originating XI roadmap session (only shown when the entry has a real <code>roadmap_session_id</code>).</li>
          </ul>
        `,
      },
      {
        id: "gameplans-module",
        title: "GamePlans",
        body: `
          <p>A <strong>GamePlan</strong> groups related plays under a shared execution context. Use one when several plays share a common outcome — a transformation program, a security incident, a mandate rebalance — so their progress, context, and decisions live in one place instead of scattered across plays.</p>

          <p style="margin-top:10px;"><strong>Two ways to create one</strong></p>
          <ul style="padding-left:18px;">
            <li><strong>Start from scratch</strong> — <em>+ New GamePlan</em> in the GamePlans index. Pick a title and type (strategy / security_incident / it_ops / hr / finance / generic). Then attach plays from the detail page.</li>
            <li><strong>Convert an inbox initiative</strong> — in the Inbox, click <em>Convert to GamePlan</em> on a complex entry. It splits the initiative into sibling child entries and groups them under one GamePlan automatically. When you later <em>Plan it</em> on a child, the resulting play inherits the GamePlan membership.</li>
          </ul>

          <p style="margin-top:10px;"><strong>The detail page has three areas</strong></p>
          <ul style="padding-left:18px;">
            <li><strong>Stage bar</strong> — situation progression indicator (where in the kill-chain / lifecycle you are). Configured per GamePlan type.</li>
            <li><strong>Plays in this GamePlan</strong> — list of member plays with their role (primary / secondary / support) and state. <em>+ Attach play</em> has two tabs: <em>Attach existing</em> (pick a play already in the workspace) or <em>From template</em> (decompose a new play from the template catalog directly into this GamePlan).</li>
            <li><strong>Shared context</strong> — append-only fact ledger. Each entry has a key, a value, a confidence (confirmed / suspected / unconfirmed), and a source (analyst_input / step_output / brain_observation / external_event). Sibling plays in the same GamePlan see these facts injected into their step briefs as <em>Campaign Context</em>.</li>
          </ul>

          <p style="margin-top:10px;"><strong>How shared context flows back into plays</strong></p>
          <ul style="padding-left:18px;">
            <li>The <em>brief composer</em> auto-fetches the top-10 context entries (confirmed first, then suspected, then unconfirmed; recent first within each tier) and prepends a <em>## Campaign Context</em> block to every step brief.</li>
            <li>On step completion, an operator can optionally add a fact to the campaign context via the <em>Add to Campaign Context (optional)</em> widget below the Complete button. Hidden on plays not in a GamePlan.</li>
            <li>Hand-driven agent steps can also write context entries by emitting <code>context_annotation</code> (single) or <code>context_annotations</code> (array) inside their structured output. Opt-in per hand-template.</li>
          </ul>

          <p style="margin-top:10px;"><strong>BAU plays</strong> (recurring operational tasks) can never be members of a GamePlan — recurring work is by definition not a campaign workstream. Enforced at the DB layer.</p>
        `,
      },
      {
        id: "plays-module",
        title: "Plays",
        body: `
          <p>The Plays index lists every play in your workspace, filterable by state. Each row shows archetype, state pill, replan count, owner, target window, and age.</p>
          <p style="margin-top:10px;">Plays that belong to a <strong>GamePlan</strong> show a <strong>⛓ GamePlan: [title]</strong> badge in the header on the play workspace — click it to jump to the GamePlan detail.</p>
          <p style="margin-top:10px;">Click any play to open the <strong>Play Workspace</strong>:</p>
          <ul style="padding-left:18px;">
            <li><strong>Steps tab</strong> — DAG-aware step list. Each row has a marker (number or ✓), title + type pill, owner + agent badge, and state-driven actions (Approve, Claim, ⚡ Run agent, ● running, Done · date).</li>
            <li><strong>Timeline tab</strong> — milestone strip with progress bar and date labels.</li>
            <li><strong>Outcome tab</strong> — KPI delta, lessons learned, attribution rollup (post-completion).</li>
            <li><strong>Governance tab</strong> — replan history, decision lineage, attribution.</li>
          </ul>
          <p style="margin-top:10px;">Right side rail: <strong>Live outcome</strong> card (KPI bar) + <strong>Activity</strong> card (last 24h via WebSocket).</p>
        `,
      },
      {
        id: "planner-module",
        title: "Planner",
        body: `
          <p>The Planner is a multi-stage flow for creating a play from a roadmap entry. State persists across navigation via <code>localStorage.playstudio.planner</code>.</p>

          <ol style="padding-left:18px; margin-top:8px;">
            <li><strong>Chooser</strong> — pick your path: Quick Launch (daily, ~5min), Template-driven (~30s, archetype match), Discovery-driven (~15-20min, novel), or Hybrid (template + Discovery refinement, ~10min).</li>
            <li><strong>Quick Launch</strong> — XAMI's draft is shown: 12 steps, ~6 weeks, 3 gates, +2.8pts target. Skim assumptions, fix one or two, launch in &lt;5min.</li>
            <li><strong>Discovery</strong> — 8-phase chat: Context → Goals → Constraints → Stakeholders → Capabilities → Risks → Plan → Sign-off. Right canvas shows the playbook materializing as you talk.</li>
            <li><strong>Template</strong> — Industry × Trade × Project archetype. Pick a template, edit, launch.</li>
            <li><strong>Hybrid</strong> — start from a template, then refine via interview.</li>
            <li><strong>Editor</strong> — visual step graph. Three views: <em>Timeline</em> (phase × week × swim lane), <em>Graph</em> (process diagram with dependency arrows), <em>Kanban</em> (status columns). Step inspector on the right.</li>
            <li><strong>Review</strong> — pre-flight checklist (Pattern Library, Stakeholders, KPI, Agent Coverage, Decision Gates), Launch CTA.</li>
          </ol>
        `,
      },
      {
        id: "templates-module",
        title: "Templates",
        body: `
          <p>The Templates Catalog is your library of play archetypes. Each template has a known step graph, agent coverage profile, and prior-run hit rate.</p>
          <ul style="padding-left:18px;">
            <li><strong>Source filter</strong> — Network (curated by Xamun), Workspace (your own), or both.</li>
            <li><strong>4-stat row</strong> — total / network / workspace / hit rate.</li>
            <li><strong>Detail drawer</strong> — phase dots, typical stakeholders, assumptions you can edit, required agents.</li>
          </ul>
        `,
      },
      {
        id: "hands-module",
        title: "Hands · Agents",
        body: `
          <p>Hands · Agents is the operations surface for the agent fleet:</p>
          <ul style="padding-left:18px;">
            <li><strong>Agent definitions</strong> — registry of agent archetypes (<em>Account Plan Builder</em>, <em>Brief Drafter</em>, <em>Cold Email Writer</em>, <em>XAMI</em>, etc.) with capabilities and dispatcher kind (<code>custom_webhook</code> / <code>cowork_mcp</code>).</li>
            <li><strong>Bindings</strong> — per-workspace links: which agent covers which capability. Status: <code>bound</code>, <code>gap</code>, or <code>oauth_expiring</code>.</li>
            <li><strong>HandsOps queue</strong> — operator's task queue: claim, complete, fail, reject. Surfaces every step requiring human action.</li>
          </ul>
        `,
      },
      {
        id: "outcomes-module",
        title: "Outcomes",
        body: `
          <p>The Outcomes surface is your retrospective view. It aggregates per-play results into governance metrics:</p>
          <ul style="padding-left:18px;">
            <li><strong>Velocity</strong> — plays completed per period, vs target.</li>
            <li><strong>Accuracy</strong> — projected KPI vs realized delta.</li>
            <li><strong>Replan frequency</strong> — by archetype, by trigger kind.</li>
            <li><strong>Decision latency</strong> — time-to-decision per gate.</li>
            <li><strong>Peer benchmarks</strong> — anonymized cohort comparison (privacy floor: cohort_min applies).</li>
          </ul>
          <p style="margin-top:10px;">Outcome rows are written automatically when a play transitions to <em>completed</em>; the outcome aggregator fires the <code>play.completed</code> listener.</p>
        `,
      },
      {
        id: "members-module",
        title: "Members",
        body: `
          <p>The Members surface lists every user with access to the workspace, their CXO role tag, their PlayStudio role (CXO / Operator / Approver / Reviewer / Staff), and their domain assignment.</p>
          <p style="margin-top:10px;"><strong>V1.1 deferred:</strong> role-edit UI and notification preferences ship in v1.1. Today: invite + remove are working; role assignment is API-only.</p>
        `,
      },
      {
        id: "subscription-module",
        title: "Subscription &amp; Settings",
        body: `
          <p>Subscription shows your plan tier, seat count, and (in v1.1) usage telemetry. Settings holds workspace-level preferences (default domain, notification cadence, integration tokens).</p>
        `,
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────── PLAYS
  {
    id: "plays",
    title: "Plays &amp; Steps",
    icon: "play",
    topics: [
      {
        id: "play-lifecycle",
        title: "Play Lifecycle",
        body: `
          <p>A play moves through a state machine. Transitions are guarded — illegal moves return 409.</p>
          <ul style="padding-left:18px;">
            <li><code>draft</code> → <strong>approve</strong> → <code>approved</code></li>
            <li><code>approved</code> → <strong>start</strong> → <code>running</code></li>
            <li><code>running</code> → <strong>pause</strong> → <code>paused</code> → <strong>resume</strong> → <code>running</code></li>
            <li><code>running</code> → <strong>request-replan</strong> → <code>replanning</code> → <strong>replan.applied</strong> → <code>running</code></li>
            <li><code>running</code> → all steps complete → <code>completed</code> (auto)</li>
            <li>Any non-terminal → <strong>abandon</strong> → <code>abandoned</code> (rationale required)</li>
          </ul>
        `,
      },
      {
        id: "step-types",
        title: "Step Types",
        body: `
          <p>Seven step types, each with type-specific UI and validation:</p>
          <ul style="padding-left:18px;">
            <li><strong>◆ decision</strong> — capture a choice with rationale + selected option. Owner is usually CXO or Approver.</li>
            <li><strong>✉ comm</strong> — draft + send a stakeholder communication. Owner is usually Operator.</li>
            <li><strong>? query</strong> — pull data (warehouse, API). Often agent-owned.</li>
            <li><strong>◦ micro_task</strong> — a small operator task (claim → complete with output).</li>
            <li><strong>⌛ wait</strong> — time gate or external dependency (e.g. "Wait for SLA window").</li>
            <li><strong>◇ milestone</strong> — checkpoint, no work; surfaces in Timeline as a major node.</li>
            <li><strong>⟳ instrumentation</strong> — set up telemetry or KPI capture. Often agent-owned.</li>
          </ul>
        `,
      },
      {
        id: "step-states",
        title: "Step States",
        body: `
          <ul style="padding-left:18px;">
            <li><code>pending</code> — has unmet upstream dependencies. Surfaces as <em>Waiting on deps</em> for human steps; ⚡ Run agent for autonomous bound steps.</li>
            <li><code>ready</code> — deps met, can be claimed (human) or dispatched (agent).</li>
            <li><code>running</code> — claimed/dispatched, in progress. Live ws event.</li>
            <li><code>awaiting_review</code> — work submitted, needs Approver sign-off.</li>
            <li><code>completed</code> — done. Output captured.</li>
            <li><code>failed</code> — agent dispatch failed or operator marked failed. Has retry button for autonomous steps.</li>
            <li><code>paused</code> — owner paused (rationale required).</li>
            <li><code>skipped</code> — replan or owner skip.</li>
          </ul>
        `,
      },
      {
        id: "replans",
        title: "Replans",
        body: `
          <p>A replan is a structured re-plan of an in-flight play. Trigger kinds: <em>agent_failure</em>, <em>stakeholder_change</em>, <em>external_blocker</em>, <em>cxo_directed</em>.</p>
          <p style="margin-top:8px;"><strong>Flow:</strong></p>
          <ol style="padding-left:18px;">
            <li>Owner clicks <em>Request replan</em> → modal captures rationale + trigger kind.</li>
            <li>Backend re-runs <code>decompose</code> → returns proposed step graph + structural diff.</li>
            <li>Replan card appears on the play workspace with <em>Approve</em> / <em>Reject</em> buttons.</li>
            <li>On approve: graph is swapped in, play state returns to <em>running</em>.</li>
          </ol>
          <p style="margin-top:8px;">Replan count is surfaced in every play list and on the Governance tab. High replan frequency by archetype is a signal Xamun uses to improve the Pattern Library.</p>
        `,
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────── AGENTS
  {
    id: "agents",
    title: "Agents",
    icon: "bot",
    topics: [
      {
        id: "agent-bindings",
        title: "Agent Bindings",
        body: `
          <p>An <strong>agent definition</strong> is the archetype (e.g. <em>Account Plan Builder</em>, capabilities = price_modeling).</p>
          <p style="margin-top:8px;">An <strong>agent binding</strong> links a definition to a specific workspace + capability with a credential reference (e.g. <em>account_plan_builder → workspace ACME → capability doc_generation → vault key xyz</em>).</p>
          <p style="margin-top:8px;">When a step is created with <code>required_capability</code>, the planner picks the matching binding. If none → capability gap (visible on the editor's left rail and the Review checklist).</p>
        `,
      },
      {
        id: "live-dispatch",
        title: "Live Dispatch",
        body: `
          <p>Three dispatcher kinds today:</p>
          <ul style="padding-left:18px;">
            <li><strong>custom_webhook</strong> — POST to a configured URL, signed with HMAC-SHA256 (header <code>X-PlayStudio-Signature: sha256=&lt;hex&gt;</code>). Used by all 3rd-party integrations and the demo agent.</li>
            <li><strong>copilot</strong> — Microsoft Graph integration. v1 ships an echo stub; v1.1 swaps in real Graph calls.</li>
            <li><strong>cowork_mcp</strong> — Xamun's internal cowork MCP server (multi-agent coordination).</li>
          </ul>
          <p style="margin-top:10px;">Dispatch flow: <code>POST /plays/:id/steps/:id/run-agent</code> → step transitions to <em>running</em> → <code>dispatch_jobs</code> row enqueued → step-dispatch worker (3s tick) picks it up → fires the request → on success, captures output and transitions step to <em>completed</em>; on failure, marks step <em>failed</em> with <code>last_error</code> and retries up to 3×.</p>
        `,
      },
      {
        id: "demo-agent",
        title: "Demo Agent",
        body: `
          <p>For the live demo, PlayStudio ships an in-process HMAC-verified webhook handler at <code>/api/_demo-agent/handler</code>. It returns canned <code>{status: "success", output: {...}}</code> envelopes per capability so end-to-end dispatch round-trips without any external dependency.</p>
          <p style="margin-top:8px;">The boot-time vault seeder hydrates <code>demo-vault://</code> binding credentials from the <code>DEMO_AGENT_SECRET</code> env var, so existing demo bindings just work after a deploy.</p>
        `,
      },
      {
        id: "capability-gaps",
        title: "Capability Gaps",
        body: `
          <p>When a play references a capability with no bound agent, it's a <em>gap</em>. Two surfaces flag this:</p>
          <ul style="padding-left:18px;">
            <li><strong>Quick Launch &amp; Editor</strong> — Capability gaps card with affected steps and a <em>+ Bind agent</em> link.</li>
            <li><strong>Review pre-flight</strong> — checklist row turns amber: "2 capability gaps — Contract redlining, FP&amp;A forecasting — manual fallback for affected steps".</li>
          </ul>
          <p style="margin-top:8px;">A gap doesn't block launch — the affected step just becomes operator-owned (manual fallback).</p>
        `,
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────── FAQ
  {
    id: "faq",
    title: "FAQ",
    icon: "help",
    topics: [
      {
        id: "where-do-initiatives-come-from",
        title: "Where do initiatives come from?",
        body: `
          <p>From XI. XI's Diagnostic + Roadmap modules promote <em>committed</em> initiatives down to PlayStudio's Inbox via the federated read. Software initiatives go to the Software Factory; the others (gtm / process / capability / org / recruitment / training / vendor / operational) come to PlayStudio.</p>
        `,
      },
      {
        id: "play-vs-gameplan",
        title: "What's the difference between a Play and a GamePlan?",
        body: `
          <p>A <strong>Play</strong> is a single execution thread — one initiative, one outcome, one step graph. Most daily work is single plays.</p>
          <p style="margin-top:8px;">A <strong>GamePlan</strong> groups multiple related plays under a shared execution context. The grouping matters when several plays share a common outcome and can feed each other context — e.g. a security incident with parallel triage / containment / forensics plays, or a transformation program with parallel comms / process / change-management plays.</p>
          <p style="margin-top:8px;">The expected ratio is roughly 80/20 — most plays are standalone. Reach for a GamePlan when you'd otherwise be copy-pasting context between plays or losing track of how they relate.</p>
        `,
      },
      {
        id: "what-is-ai-assessment",
        title: "What does the AI Assessment pill mean?",
        body: `
          <p>The AI Assessment pill on each Inbox card is an advisory recommendation: <em>Single play</em> vs <em>GamePlan</em> (multi-workstream campaign). It's never auto-fired — you still pick the action.</p>
          <p style="margin-top:8px;">It's computed automatically right after XI promotes an initiative into your Inbox, using the entry's title, intent, archetype, cross-team flag, target window, and projected KPI. The confidence % shows how sure the model is.</p>
          <p style="margin-top:8px;">Hover the pill to see the reasoning. Use <em>Re-assess with AI</em> in the card's action row to refresh the recommendation against the latest workspace LLM key.</p>
          <p style="margin-top:8px;">Pills don't appear on template-direct entries (those are inherently plays), on recurrence templates, or on entries already split into a GamePlan — the classifier skips those by design.</p>
        `,
      },
      {
        id: "what-is-the-coach",
        title: "What is The Coach?",
        body: `
          <p>The Coach is the LLM brain that watches each GamePlan and proposes orchestration actions — when to spawn a new play, when to escalate severity, what facts to observe into the shared context, what to flag to a human.</p>
          <p style="margin-top:8px;">It fires on workspace events (a step completes, a context entry lands) and on a 60-second sweep for idle campaigns. Each cycle it reads the campaign intent, the play graph, the context ledger, and the available template catalog, then proposes zero or more actions.</p>
          <p style="margin-top:8px;"><strong>Auto vs Propose:</strong> per-workspace policy decides which action types fire automatically and which wait for CXO approval. Defaults: <code>observe</code> (synthesises a fact) auto-fires; <code>spawn_play</code> / <code>escalate_severity</code> / <code>flag_human</code> propose and surface on the Coach panel of the GamePlan detail page.</p>
          <p style="margin-top:8px;">Click <em>Ask the Coach</em> on a GamePlan detail to trigger a manual reasoning cycle. Every proposal is auditable — the LLM's reasoning is stored verbatim alongside the action.</p>
        `,
      },
      {
        id: "how-do-i-create-a-gameplan",
        title: "How do I create a GamePlan?",
        body: `
          <p>Two paths, depending on whether you already have something to group:</p>
          <ul style="padding-left:18px;">
            <li><strong>From the GamePlans nav item</strong> — click <em>+ New GamePlan</em>, fill title / type / description. Lands you on the detail page; from there <em>+ Attach play</em> lets you either attach an existing play or pick a template to spawn a new play directly into the GamePlan.</li>
            <li><strong>From the Inbox</strong> — click <em>Convert to GamePlan</em> on a complex initiative. It splits the initiative into sibling child entries (you supply 2-10 child titles) and groups them under one GamePlan. Children inherit the GamePlan membership; when you <em>Plan it</em> on a child later, the resulting play auto-joins.</li>
          </ul>
        `,
      },
      {
        id: "can-i-create-a-play-without-an-initiative",
        title: "Can I create a play without an XI initiative?",
        body: `
          <p>Not in v1. Every play must trace to a roadmap entry. The contract is <em>"no orphan plays"</em> — it's how the strategy map stays connected to execution outcomes.</p>
        `,
      },
      {
        id: "what-happens-after-launch",
        title: "What happens after I click Launch play?",
        body: `
          <ol style="padding-left:18px;">
            <li>Play state: <code>draft</code> → <code>approved</code> → <code>running</code>.</li>
            <li>Roadmap entry status: <code>committed</code> → <code>in_execution</code>.</li>
            <li>Step 1 propagates from <em>pending</em> → <em>ready</em> (no upstream deps).</li>
            <li>Autonomous steps fire automatically; human steps surface in HandsOps queue.</li>
            <li>WebSocket fan-out lights up the activity rail in real time.</li>
          </ol>
        `,
      },
      {
        id: "what-if-an-agent-fails",
        title: "What if an agent dispatch fails?",
        body: `
          <p>The step transitions to <em>failed</em> and the row gets a <em>⚡ Retry</em> button. The dispatch worker also retries automatically up to 3× with backoff (3s / 30s / 5min). After 3 failures, the step stays failed and surfaces in the operator queue for manual handling.</p>
        `,
      },
      {
        id: "open-in-xi-not-shown",
        title: "Why isn't \"Open in XI\" showing on some entries?",
        body: `
          <p>It only renders when the entry has a real XI roadmap session id (<code>signal_lineage.roadmap_session_id</code>). Demo-seeded entries don't have one, so the link is hidden to avoid landing you on the wrong company's XI page.</p>
        `,
      },
      {
        id: "voice-mode",
        title: "Where's voice mode in Discovery?",
        body: `
          <p>Deferred to v1.1. The voice / text toggle is in the UI but voice is wired to a stub. Text mode works end-to-end.</p>
        `,
      },
    ],
  },
];

/**
 * Get the default help section based on current pathname.
 */
export function getDefaultSection(pathname: string): string {
  if (pathname.includes("/plays/")) return "plays";
  if (pathname.includes("/planner")) return "modules";
  if (pathname.includes("/inbox")) return "modules";
  if (pathname.includes("/templates")) return "modules";
  if (pathname.includes("/agents")) return "agents";
  if (pathname.includes("/operator")) return "modules";
  if (pathname.includes("/outcomes")) return "modules";
  if (pathname.includes("/members") || pathname.includes("/subscription") || pathname.includes("/settings")) {
    return "modules";
  }
  if (pathname.includes("/discovery")) return "plays";
  return "welcome";
}

/**
 * Get the default help topic based on current pathname.
 */
export function getDefaultTopic(pathname: string): string {
  if (pathname.includes("/plays/")) return "play-lifecycle";
  if (pathname.includes("/planner")) return "planner-module";
  if (pathname.includes("/inbox")) return "inbox-module";
  if (pathname.includes("/templates")) return "templates-module";
  if (pathname.includes("/agents")) return "agent-bindings";
  if (pathname.includes("/operator")) return "hands-module";
  if (pathname.includes("/outcomes")) return "outcomes-module";
  if (pathname.includes("/members")) return "members-module";
  if (pathname.includes("/subscription") || pathname.includes("/settings")) {
    return "subscription-module";
  }
  if (pathname.includes("/discovery")) return "step-types";
  return "getting-started";
}
